/**
 * Copyright Aquera Inc 2023
 *
 * This source code is licensed under the BSD-3-Clause license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { css, PropertyValues } from 'lit';
import {
  LitElement,
  html,
  property,
  CSSResultArray,
  TemplateResult,
} from 'lit-element';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styles } from './nile-drawer.css';

/**
 * Nile icon component.
 *
 * @tag nile-drawer
 *
 */
@customElement('nile-drawer')
export class NileDrawer extends LitElement {
  static override styles = css`
    @keyframes slide-from-right {
      0% {
        transform: translateX(100%);
      }

      100% {
        transform: translateX(0);
      }
    }
    :not(:defined) {
      visibility: hidden;
    }
    :host {
      box-sizing: border-box;
      position: fixed;
      display: none;
      top: 0;
      right: 0;
      bottom: 0;
      width: 424px;
      padding: var(--bl-size-xl);
      padding-top: max(env(safe-area-inset-top), var(--bl-size-xl));
      padding-right: max(env(safe-area-inset-right), var(--bl-size-xl));
      padding-bottom: max(env(safe-area-inset-bottom), var(--bl-size-xl));
      background: var(--bl-color-primary-background);
      box-shadow: var(--bl-size-xs) 0 var(--bl-size-2xl) rgba(0 0 0 / 50%);
      transform: translateX(0); //translateX(100%);
      transition: transform var(--bl-drawer-animation-duration, 0.25s);
      z-index: 999;
      background-color: #fff;
      border-left: 1px solid #e5e5e5;
    }

    :host([open]) .drawer {
      transform: translateX(0);
      animation: slide-from-right var(--bl-drawer-animation-duration, 0.25s);
      position: fixed;
      box-shadow: 0px 14px 28px rgb(119 125 130 / 15%);
    }

    .nds-drawer {
      box-sizing: border-box;
      position: fixed;
      display: flex;
      top: 0;
      right: 0;
      bottom: 0;
      width: 424px;
      padding: var(--bl-size-xl);
      padding-top: max(env(safe-area-inset-top), var(--bl-size-xl));
      padding-right: max(env(safe-area-inset-right), var(--bl-size-xl));
      padding-bottom: max(env(safe-area-inset-bottom), var(--bl-size-xl));
      background: var(--bl-color-primary-background);
      box-shadow: var(--bl-size-xs) 0 var(--bl-size-2xl) rgba(0 0 0 / 50%);
      transform: translateX(100%);
      transition: transform var(--bl-drawer-animation-duration, 0.25s);
      z-index: 999;
    }

    iframe {
      height: 100%;
      width: 100%;
      border: none;
    }

    .container {
      display: flex;
      flex-direction: column;
      gap: var(--bl-size-xl);
      flex: 1;
    }

    header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: var(--bl-size-2xs);
      background-color: white;
    }

    header .header-buttons {
      display: flex;
      gap: 24px;
      margin-left: auto;
    }

    header h2 {
      font: var(--bl-font-title-1-medium);
      color: var(--bl-color-secondary);
      overflow: hidden;
      margin: 0;
      padding: 0;
    }

    .content {
      flex: 1;
      overflow-y: scroll;
    }

    .iframe-content {
      flex: 1;
    }

    @media only screen and (max-width: 424px) {
      :host([open]) .drawer {
        width: calc(100vw - 24px);
      }
    }

    .header-section {
      width: 100%;
      background-color: #e5e5e5;
    }
    .nds-drawer--closed {
      display: none;
    }
  `;

  /**
   * Sets drawer open-close status
   */
  @property({ type: Boolean, reflect: true })
  open = false;

  //   private domExistenceSchedule: number;

  private toggleDialogHandler() {
    if (this.open) {
      this.domExistence = true;
      // FIXME: Allow events without payload
      //   this.onOpen('');
    } else {
      // Give some time for exit animation
      //   this.domExistenceSchedule = window.setTimeout(() => {
      //     this.domExistence = false;
      //   }, 1000);
    }
  }

  @state() private domExistence = false;

  connectedCallback() {
    super.connectedCallback();
    window?.addEventListener('nile-drawer-open', event => {
      if (event.target !== this) this.closeDrawer();
    });
  }

  private closeDrawer() {
    this.open = false;
  }

  updated(changedProperties: PropertyValues<this>) {
    if (changedProperties.has('open')) {
      this.toggleDialogHandler();
    }
  }

  get buttonClassMap() {
    return {
      'nds-drawer--open': this.open,
      'nds-drawer--closed': !this.open,
    };
  }

  /* #endregion */

  /* #region Methods */

  /**
   * Render method
   * @slot This is a slot test
   */
  public render(): TemplateResult {
    return html`
      <div class="btn ${classMap(this.buttonClassMap)}">
        <slot name="header">
          <div class="header-section">
            <nile-heading name="Edit Config"></nile-heading>
          </div>
        </slot>
        <slot name="content"></slot>
        <slot name="footer"></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nile-drawer': NileDrawer;
  }
}
